import { useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Check, MessageCircle, PhoneOff, CalendarClock, TrendingUp, Sparkles } from 'lucide-react';
import { submitBetaSignup } from '../api/beta';
import { validateBetaPhone, validateBetaEmail, validateBetaFullName, normalizeBetaPhone } from '../utils/formValidation';
import PhoneMockup from '../components/iPhoneMockup';

const BUSINESS_TYPES = [
  { value: 'cosmetics', label: 'קוסמטיקה' },
  { value: 'hair', label: 'שיער' },
  { value: 'nails', label: 'ציפורניים' },
  { value: 'makeup', label: 'איפור' },
  { value: 'other', label: 'אחר' },
];

const MONTHLY_OPTIONS = [
  { value: '0-10', label: '0-10' },
  { value: '10-50', label: '10-50' },
  { value: '50-100', label: '50-100' },
  { value: '100+', label: '100+' },
];

const CALENDAR_OPTIONS = [
  { value: 'google', label: 'יומן גוגל' },
  { value: 'paper', label: 'יומן פיזי' },
  { value: 'app', label: 'אפליקציה אחרת' },
  { value: 'other', label: 'אחר' },
];

const PHONE_OS_OPTIONS = [
  { value: 'iphone', label: 'iPhone' },
  { value: 'android', label: 'Android (Samsung/Xiaomi/Pixel)' },
  { value: 'other', label: 'אחר' },
];

const BOOKING_OPTIONS = [
  { value: 'phone', label: 'שיחות טלפון' },
  { value: 'whatsapp', label: 'הודעות וואטסאפ' },
  { value: 'online_link', label: 'קישור להזמנה אונליין / אפליקציה' },
  { value: 'other', label: 'אחר' },
];

const FEATURES = [
  { icon: MessageCircle, title: 'עונה על הכל', text: 'הבוט יודע את השעות, המחירים, השירותים והמיקום שלך – ועונה ללקוחות על כל שאלה בוואטסאפ.' },
  { icon: PhoneOff, title: 'שקט בזמן עבודה', text: 'התרכז/י בלקוח/ה שמולך, הבוט יענה לכל השאר.' },
  { icon: CalendarClock, title: 'זמינות 24/7', text: 'לקוחות קובעים תורים גם כשאת/ה ישן/ה או בחופשה.' },
  { icon: TrendingUp, title: 'יותר הכנסות', text: 'כשעונים מיד ללקוח/ה, הוא לא בורח למתחרים.' },
];

const HOW_IT_WORKS = [
  { step: 1, text: 'את/ה מחבר/ת את הבוט לווטסאפ ונותנ/ת לו את פרטי העסק.' },
  { step: 2, text: 'הוא מסתנכרן עם יומן גוגל ועונה ללקוחות על כל שאלה.' },
  { step: 3, text: 'הלקוחות מתכתבים, מקבלים מידע, וקובעים תורים לבד!' },
];

const MOTION_PROPS = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5 },
};

export default function BetaLandingPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const leadIdRef = useRef<string | null>(null);
  const savePromiseRef = useRef<Promise<string | null> | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);

  const [hasFacebook, setHasFacebook] = useState<boolean | null>(null);
  const [businessType, setBusinessType] = useState('');
  const [monthlyAppointments, setMonthlyAppointments] = useState('');
  const [calendarSystem, setCalendarSystem] = useState('');
  const [phoneOs, setPhoneOs] = useState('');
  const [bookingMethod, setBookingMethod] = useState('');

  const [businessTypeOther, setBusinessTypeOther] = useState('');
  const [calendarOther, setCalendarOther] = useState('');
  const [phoneOsOther, setPhoneOsOther] = useState('');
  const [bookingOnlineLinkSystem, setBookingOnlineLinkSystem] = useState('');
  const [bookingOther, setBookingOther] = useState('');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  const save = useCallback(async (payload: Record<string, unknown>) => {
    const keys = Object.keys(payload).join(',');
    setSaving(keys);
    setSaveError(null);
    const prevPromise = savePromiseRef.current ?? Promise.resolve(leadIdRef.current);
    const myPromise = prevPromise
      .then(async (existingId) => {
        const idToUse = existingId ?? leadIdRef.current;
        const payloadWithId = idToUse ? { ...payload, id: idToUse } : payload;
        const res = await submitBetaSignup(payloadWithId);
        leadIdRef.current = res.id;
        setLeadId(res.id);
        return res.id;
      })
      .catch((e) => {
        console.error('Beta signup save failed:', e);
        const msg = e?.response?.data?.detail ?? (typeof e?.message === 'string' ? e.message : 'שגיאה בשמירה. נסה שוב מאוחר יותר.');
        setSaveError(Array.isArray(msg) ? msg.join(' ') : msg);
        return leadIdRef.current;
      })
      .finally(() => setSaving(null));
    savePromiseRef.current = myPromise;
    await myPromise;
  }, []);

  const validateField = useCallback((field: string, value: string) => {
    if (field === 'full_name') {
      const r = validateBetaFullName(value);
      setFieldErrors((p) => ({ ...p, full_name: r.valid ? '' : r.message }));
      return r.valid;
    }
    if (field === 'phone') {
      const r = validateBetaPhone(value);
      setFieldErrors((p) => ({ ...p, phone: r.valid ? '' : r.message }));
      return r.valid;
    }
    if (field === 'email') {
      const r = validateBetaEmail(value);
      setFieldErrors((p) => ({ ...p, email: r.valid ? '' : r.message }));
      return r.valid;
    }
    return true;
  }, []);

  const isStep1Valid = validateBetaFullName(fullName).valid && validateBetaPhone(phone).valid;

  const handleBlur = useCallback(
    (field: string, value: string | boolean) => {
      if (typeof value === 'string') validateField(field, value);
      const hasId = leadIdRef.current ?? leadId;
      const base: Record<string, unknown> = hasId ? {} : { phone: phone ? normalizeBetaPhone(phone) : undefined, email: email || undefined };
      if (!hasId && !phone && !email && field !== 'phone' && field !== 'email') return;
      let payloadValue: unknown = value;
      if (field === 'phone' && typeof value === 'string' && value.trim()) payloadValue = normalizeBetaPhone(value);
      const payload = { ...base, [field]: payloadValue };
      if (field === 'full_name' && (!phone && !email)) return;
      if ((field === 'phone' || field === 'email') && !value) return;
      save(payload);
    },
    [leadId, phone, email, save, validateField]
  );

  const handleStep1Next = async () => {
    if (!isStep1Valid) return;
    const payload: Record<string, unknown> = {
      full_name: fullName.trim() || undefined,
      phone: normalizeBetaPhone(phone),
      email: email.trim() || undefined,
      marketing_consent: marketingConsent,
    };
    setSaving('step1');
    setSaveError(null);
    try {
      const existingId = leadIdRef.current ?? (savePromiseRef.current ? await savePromiseRef.current : null);
      const payloadWithId = existingId ? { ...payload, id: existingId } : payload;
      const res = await submitBetaSignup(payloadWithId);
      leadIdRef.current = res.id;
      setLeadId(res.id);
      savePromiseRef.current = Promise.resolve(res.id);
      setStep(2);
    } catch (e: unknown) {
      console.error('Beta signup failed:', e);
      const err = e as { response?: { data?: { detail?: string | string[] } }; message?: string };
      const msg = err?.response?.data?.detail ?? (typeof err?.message === 'string' ? err.message : 'שגיאה בשליחה. נסה שוב מאוחר יותר.');
      setSaveError(Array.isArray(msg) ? msg.join(' ') : msg);
    } finally {
      setSaving(null);
    }
  };

  const saveStep2 = useCallback(
    (payload: Record<string, unknown>) => {
      const id = leadIdRef.current ?? leadId;
      if (!id) return;
      save({ ...payload, id });
    },
    [leadId, save]
  );

  const getResolvedValue = useCallback((field: string, baseValue: string, customValue: string): string => {
    if (field === 'business_type') return baseValue === 'other' ? (customValue.trim() || 'אחר') : baseValue;
    if (field === 'calendar_system') return baseValue === 'other' ? (customValue.trim() || 'אחר') : baseValue;
    if (field === 'phone_os') return baseValue === 'other' ? (customValue.trim() || 'אחר') : baseValue;
    if (field === 'booking_method') {
      if (baseValue === 'online_link') return customValue.trim() || 'Online Link';
      if (baseValue === 'other') return customValue.trim() || 'אחר';
      return baseValue;
    }
    return baseValue;
  }, []);

  const handleStep2Change = useCallback(
    (field: string, value: string | boolean) => {
      if (field === 'has_facebook') {
        const val = value === true;
        setHasFacebook(val);
        saveStep2({ has_facebook: val });
        return;
      }
      const strVal = value as string;
      if (field === 'business_type') {
        setBusinessType(strVal);
        saveStep2({ business_type: strVal === 'other' ? (businessTypeOther.trim() || 'אחר') : strVal });
      } else if (field === 'calendar_system') {
        setCalendarSystem(strVal);
        saveStep2({ calendar_system: strVal === 'other' ? (calendarOther.trim() || 'אחר') : strVal });
      } else if (field === 'phone_os') {
        setPhoneOs(strVal);
        saveStep2({ phone_os: strVal === 'other' ? (phoneOsOther.trim() || 'אחר') : strVal });
      } else if (field === 'booking_method') {
        setBookingMethod(strVal);
        const resolved = strVal === 'online_link' ? (bookingOnlineLinkSystem.trim() || 'Online Link') : strVal === 'other' ? (bookingOther.trim() || 'אחר') : strVal;
        saveStep2({ booking_method: resolved });
      } else {
        if (field === 'monthly_appointments') setMonthlyAppointments(strVal);
        saveStep2({ [field]: strVal });
      }
    },
    [saveStep2, businessTypeOther, calendarOther, phoneOsOther, bookingOnlineLinkSystem, bookingOther]
  );

  const handleCustomOtherBlur = useCallback(
    (field: string, value: string) => {
      if (field === 'business_type_other' && businessType === 'other') saveStep2({ business_type: value.trim() || 'אחר' });
      if (field === 'calendar_other' && calendarSystem === 'other') saveStep2({ calendar_system: value.trim() || 'אחר' });
      if (field === 'phone_os_other' && phoneOs === 'other') saveStep2({ phone_os: value.trim() || 'אחר' });
      if (field === 'booking_online_link' && bookingMethod === 'online_link') saveStep2({ booking_method: value.trim() || 'Online Link' });
      if (field === 'booking_other' && bookingMethod === 'other') saveStep2({ booking_method: value.trim() || 'אחר' });
    },
    [saveStep2, businessType, calendarSystem, phoneOs, bookingMethod]
  );

  const handleFinish = async () => {
    if (!leadId || hasFacebook === null) return;
    setSaving('finish');
    setSaveError(null);
    const resolvedBusinessType = getResolvedValue('business_type', businessType, businessTypeOther);
    const resolvedCalendar = getResolvedValue('calendar_system', calendarSystem, calendarOther);
    const resolvedPhoneOs = getResolvedValue('phone_os', phoneOs, phoneOsOther);
    const resolvedBooking = bookingMethod === 'online_link' ? (bookingOnlineLinkSystem.trim() || 'Online Link') : bookingMethod === 'other' ? (bookingOther.trim() || 'אחר') : bookingMethod;
    try {
      await submitBetaSignup({
        id: leadId,
        has_facebook: hasFacebook ?? undefined,
        business_type: resolvedBusinessType || undefined,
        monthly_appointments: monthlyAppointments || undefined,
        calendar_system: resolvedCalendar || undefined,
        phone_os: resolvedPhoneOs || undefined,
        booking_method: resolvedBooking || undefined,
        is_fully_submitted: true,
      });
      setSubmitted(true);
    } catch (e: unknown) {
      console.error('Beta signup finish failed:', e);
      const err = e as { response?: { data?: { detail?: string | string[] } }; message?: string };
      const msg = err?.response?.data?.detail ?? (typeof err?.message === 'string' ? err.message : 'שגיאה בשליחה. נסה שוב מאוחר יותר.');
      setSaveError(Array.isArray(msg) ? msg.join(' ') : msg);
    } finally {
      setSaving(null);
    }
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  if (submitted) {
    return (
      <div className="min-h-screen beta-mesh-bg flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }} className="max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#E17F88] text-white mb-6 shadow-lg shadow-[#E17F88]/30">
            <Check className="w-10 h-10" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3 font-[Heebo,sans-serif]">תודה שהצטרפת!</h1>
          <p className="text-gray-600 text-lg leading-relaxed">נחזור אליך בהקדם עם פרטים.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen beta-mesh-bg pb-24">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/70 backdrop-blur-md border-b border-white/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/aibizi-logo-icon.png" alt="AiBizi" className="w-8 h-8" />
            <span className="text-xl font-bold text-gray-900 font-[Heebo,sans-serif]">AiBizi</span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-12 lg:py-20 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col">
          {/* 1. Headline + Subheadline: first, before phone and form */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="order-1 mb-8 lg:mb-12"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.15] mb-6 font-[Heebo,sans-serif]">
              העוזר האישי שלך בוואטסאפ
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-xl">
              עונה ללקוחות 24/7 על שעות, מחירים ושירותים. קובע תורים אוטומטית – בזמן שאת/ה עובד/ת.
            </p>
          </motion.div>

          {/* 2. Phone + Form: phone then form on mobile; side-by-side on desktop */}
          <div className="order-2 flex flex-col lg:flex-row lg:gap-16 lg:items-start">
            {/* iPhone Mockup: second on mobile, left on desktop */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="flex justify-center mb-10 lg:mb-0 lg:justify-end min-w-0 lg:flex-1"
            >
              <div className="w-full max-w-[90vw] flex justify-center">
                <PhoneMockup />
              </div>
            </motion.div>

            {/* Form: third on mobile, right on desktop */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:flex-1 w-full"
            >
            {/* Glassmorphism Form Card */}
            <form
              ref={formRef}
              id="beta-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (step === 1 && isStep1Valid && saving !== 'step1') handleStep1Next();
                else if (step === 2 && saving !== 'finish') handleFinish();
              }}
              className="bg-white/80 backdrop-blur-md border border-white/20 shadow-xl rounded-2xl p-6 sm:p-8"
            >
              <div className="mb-6">
                <div className="h-2 bg-gray-200/80 rounded-full overflow-hidden">
                  <div className="h-full bg-[#E17F88] transition-all duration-300 rounded-full" style={{ width: step === 1 ? '50%' : '100%' }} />
                </div>
                <p className="text-sm text-gray-500 mt-2">שלב {step} מתוך 2</p>
              </div>

              {saveError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm" role="alert">
                  {saveError}
                </div>
              )}

              {step === 1 && (
                <>
                  <h2 className="text-xl font-semibold text-gray-900 mb-1 font-[Heebo,sans-serif]">היכנס/י לרשימת המתנה</h2>
                  <p className="text-sm text-gray-600 mb-6">מלא/י את הפרטים ונחזור אליך </p>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="beta-full-name" className="block text-sm font-medium text-gray-800 mb-1.5">שם מלא *</label>
                      <div className="relative">
                        <input
                          id="beta-full-name"
                          name="name"
                          type="text"
                          autoComplete="name"
                          value={fullName}
                          onChange={(e) => { setFullName(e.target.value); if (fieldErrors.full_name) validateField('full_name', e.target.value); }}
                          onBlur={() => { validateField('full_name', fullName); fullName.trim() && handleBlur('full_name', fullName.trim()); }}
                          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E17F88]/50 bg-white/90 text-gray-800 ${fieldErrors.full_name ? 'border-red-500' : 'border-gray-200'}`}
                          placeholder="השם המלא שלך"
                        />
                        {saving === 'full_name' && <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#E17F88]" />}
                        {fieldErrors.full_name && <p className="mt-1 text-sm text-red-600">{fieldErrors.full_name}</p>}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="beta-phone" className="block text-sm font-medium text-gray-800 mb-1.5">טלפון *</label>
                      <div className="relative">
                        <input
                          id="beta-phone"
                          name="phone"
                          type="tel"
                          autoComplete="tel"
                          value={phone}
                          onChange={(e) => { setPhone(e.target.value); if (fieldErrors.phone) validateField('phone', e.target.value); }}
                          onBlur={() => { validateField('phone', phone); phone.trim() && handleBlur('phone', phone.trim()); }}
                          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E17F88]/50 bg-white/90 text-gray-800 ${fieldErrors.phone ? 'border-red-500' : 'border-gray-200'}`}
                          placeholder="05X-XXXXXXX"
                        />
                        {saving === 'phone' && <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#E17F88]" />}
                        {fieldErrors.phone && <p className="mt-1 text-sm text-red-600">{fieldErrors.phone}</p>}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="beta-email" className="block text-sm font-medium text-gray-800 mb-1.5">אימייל (מומלץ)</label>
                      <div className="relative">
                        <input
                          id="beta-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          value={email}
                          onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) validateField('email', e.target.value); }}
                          onBlur={() => { validateField('email', email); handleBlur('email', email.trim()); }}
                          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E17F88]/50 bg-white/90 text-gray-800 ${fieldErrors.email ? 'border-red-500' : 'border-gray-200'}`}
                          placeholder="your@email.com"
                        />
                        {saving === 'email' && <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#E17F88]" />}
                        {fieldErrors.email && <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>}
                      </div>
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={marketingConsent} onChange={(e) => { const v = e.target.checked; setMarketingConsent(v); if (phone || email) { const id = leadIdRef.current ?? leadId; const base = id ? {} : { phone: phone ? normalizeBetaPhone(phone) : undefined, email: email || undefined }; save(id ? { marketing_consent: v, id } : { ...base, marketing_consent: v }); } }} className="mt-1 w-4 h-4 rounded border-gray-300 text-[#E17F88] focus:ring-[#E17F88]" />
                      <span className="text-sm text-gray-700">אני מסכים/ה לקבל עדכונים שיווקיים</span>
                    </label>
                  </div>

                  <button type="submit" disabled={!isStep1Valid || saving === 'step1'} className="mt-6 w-full py-3.5 px-4 bg-[#E17F88] hover:bg-[#D16F78] disabled:opacity-50 disabled:cursor-not-allowed disabled:animate-none text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 animate-beta-pulse shadow-lg shadow-[#E17F88]/25">
                    {saving === 'step1' ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                    המשך לשלב הבא
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  <h2 className="text-xl font-semibold text-gray-900 mb-1 font-[Heebo,sans-serif]">כמה שאלות קצרות</h2>
                  <p className="text-sm text-gray-600 mb-6">כל השדות אופציונליים מלבד השאלה הראשונה</p>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-800 mb-2">יש לך דף עסקי בפייסבוק? *</label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="has_facebook"
                            checked={hasFacebook === true}
                            onChange={() => handleStep2Change('has_facebook', true)}
                            className="w-4 h-4 text-[#E17F88] focus:ring-[#E17F88]"
                          />
                          <span className="text-gray-700">כן</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="has_facebook"
                            checked={hasFacebook === false}
                            onChange={() => handleStep2Change('has_facebook', false)}
                            className="w-4 h-4 text-[#E17F88] focus:ring-[#E17F88]"
                          />
                          <span className="text-gray-700">לא</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-800 mb-2">סוג העסק</label>
                      <select value={businessType} onChange={(e) => { const v = e.target.value; setBusinessType(v); if (v !== 'other') setBusinessTypeOther(''); handleStep2Change('business_type', v); }} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E17F88]/50 bg-white/90 text-gray-800">
                        <option value="">בחר...</option>
                        {BUSINESS_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      {businessType === 'other' && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3">
                          <input type="text" value={businessTypeOther} onChange={(e) => setBusinessTypeOther(e.target.value)} onBlur={(e) => handleCustomOtherBlur('business_type_other', e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E17F88]/50 bg-white/90 text-gray-800" placeholder="פרט/י..." />
                        </motion.div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-800 mb-2">לקוחות בחודש (בערך)</label>
                      <div className="flex flex-wrap gap-2">
                        {MONTHLY_OPTIONS.map((o) => (
                          <button key={o.value} type="button" onClick={() => handleStep2Change('monthly_appointments', o.value)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${monthlyAppointments === o.value ? 'bg-[#E17F88] text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{o.label}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-800 mb-2">איך מתנהל היומן שלך היום?</label>
                      <div className="space-y-2">
                        {CALENDAR_OPTIONS.map((o) => (
                          <label key={o.value} className="flex items-center gap-3 cursor-pointer">
                            <input type="radio" name="calendar" checked={calendarSystem === o.value} onChange={() => { setCalendarSystem(o.value); if (o.value !== 'other') setCalendarOther(''); handleStep2Change('calendar_system', o.value); }} className="w-4 h-4 text-[#E17F88] focus:ring-[#E17F88]" />
                            <span className="text-gray-700">{o.label}</span>
                          </label>
                        ))}
                      </div>
                      {calendarSystem === 'other' && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3">
                          <input type="text" value={calendarOther} onChange={(e) => setCalendarOther(e.target.value)} onBlur={(e) => handleCustomOtherBlur('calendar_other', e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E17F88]/50 bg-white/90 text-gray-800" placeholder="פרט/י..." />
                        </motion.div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-800 mb-2">סוג הטלפון</label>
                      <div className="space-y-2">
                        {PHONE_OS_OPTIONS.map((o) => (
                          <label key={o.value} className="flex items-center gap-3 cursor-pointer">
                            <input type="radio" name="phone_os" checked={phoneOs === o.value} onChange={() => { setPhoneOs(o.value); if (o.value !== 'other') setPhoneOsOther(''); handleStep2Change('phone_os', o.value); }} className="w-4 h-4 text-[#E17F88] focus:ring-[#E17F88]" />
                            <span className="text-gray-700">{o.label}</span>
                          </label>
                        ))}
                      </div>
                      {phoneOs === 'other' && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3">
                          <input type="text" value={phoneOsOther} onChange={(e) => setPhoneOsOther(e.target.value)} onBlur={(e) => handleCustomOtherBlur('phone_os_other', e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E17F88]/50 bg-white/90 text-gray-800" placeholder="פרט/י..." />
                        </motion.div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-800 mb-2">איך קובעים אצלך תורים היום?</label>
                      <div className="space-y-2">
                        {BOOKING_OPTIONS.map((o) => (
                          <label key={o.value} className="flex items-center gap-3 cursor-pointer">
                            <input type="radio" name="booking" checked={bookingMethod === o.value} onChange={() => { setBookingMethod(o.value); if (o.value !== 'online_link') setBookingOnlineLinkSystem(''); if (o.value !== 'other') setBookingOther(''); handleStep2Change('booking_method', o.value); }} className="w-4 h-4 text-[#E17F88] focus:ring-[#E17F88]" />
                            <span className="text-gray-700">{o.label}</span>
                          </label>
                        ))}
                      </div>
                      {bookingMethod === 'online_link' && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3">
                          <input type="text" value={bookingOnlineLinkSystem} onChange={(e) => setBookingOnlineLinkSystem(e.target.value)} onBlur={(e) => handleCustomOtherBlur('booking_online_link', e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E17F88]/50 bg-white/90 text-gray-800" placeholder="באיזו מערכת את משתמשת? (למשל: Tor4You, Calendly...)" />
                        </motion.div>
                      )}
                      {bookingMethod === 'other' && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3">
                          <input type="text" value={bookingOther} onChange={(e) => setBookingOther(e.target.value)} onBlur={(e) => handleCustomOtherBlur('booking_other', e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E17F88]/50 bg-white/90 text-gray-800" placeholder="פרט/י..." />
                        </motion.div>
                      )}
                    </div>
                  </div>

                  <button type="submit" disabled={saving === 'finish' || hasFacebook === null} className="mt-8 w-full py-3.5 px-4 bg-[#E17F88] hover:bg-[#D16F78] disabled:opacity-50 disabled:cursor-not-allowed disabled:animate-none text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 animate-beta-pulse shadow-lg shadow-[#E17F88]/25">
                    {saving === 'finish' ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                    סיום ושליחה
                  </button>
                </>
              )}
            </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Strip - Trusted by */}
      <section className="py-12 px-4 border-y border-gray-200/60 bg-white/40">
        <div className="max-w-4xl mx-auto">
          <motion.p {...MOTION_PROPS} className="text-center text-sm font-medium text-gray-500 mb-6">Trusted by beauty businesses</motion.p>
          <motion.div {...MOTION_PROPS} className="flex flex-wrap justify-center items-center gap-8 sm:gap-12 opacity-70">
            {[MessageCircle, CalendarClock, TrendingUp].map((Icon, i) => (
              <Icon key={i} className="w-8 h-8 text-[#E17F88]" />
            ))}
          </motion.div>
        </div>
      </section>

      {/* How it works - Cards */}
      <section className="py-20 px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.h2 {...MOTION_PROPS} className="text-3xl font-bold text-gray-900 text-center mb-4 font-[Heebo,sans-serif]">איך זה עובד?</motion.h2>
          <motion.p {...MOTION_PROPS} className="text-gray-600 text-center mb-16 max-w-2xl mx-auto">שלושה צעדים פשוטים אל העסק החכם שלך</motion.p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {HOW_IT_WORKS.map((item, i) => (
              <motion.div
                key={item.step}
                {...MOTION_PROPS}
                transition={{ ...MOTION_PROPS.transition, delay: i * 0.1 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg shadow-gray-200/50 border border-white/60 hover:shadow-xl hover:shadow-[#E17F88]/10 hover:border-[#E17F88]/20 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#E17F88]/15 flex items-center justify-center mb-6">
                  <span className="text-2xl font-bold text-[#E17F88]">{item.step}</span>
                </div>
                <p className="text-gray-800 text-lg leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 lg:px-8 bg-white/30">
        <div className="max-w-6xl mx-auto">
          <motion.h2 {...MOTION_PROPS} className="text-3xl font-bold text-gray-900 text-center mb-4 font-[Heebo,sans-serif]">למה בעלי עסקים עוברים לבוט?</motion.h2>
          <motion.p {...MOTION_PROPS} className="text-gray-600 text-center mb-16">ארבע סיבות להצטרף לנבחרת</motion.p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  {...MOTION_PROPS}
                  transition={{ ...MOTION_PROPS.transition, delay: i * 0.08 }}
                  className="bg-white rounded-2xl p-6 shadow-md shadow-gray-200/50 border border-white/80 hover:shadow-lg hover:shadow-[#E17F88]/10 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#E17F88]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#E17F88]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 font-[Heebo,sans-serif]">{feature.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{feature.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer FOMO */}
      <footer className="py-20 px-4">
        <motion.div {...MOTION_PROPS} className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#E17F88]/10 rounded-full mb-6">
            <Sparkles className="w-5 h-5 text-[#E17F88]" />
            <span className="text-gray-800 font-medium">אנחנו פותחים רק 2 מקומות לפיילוט הקרוב.</span>
          </div>
          <button onClick={scrollToForm} className="py-4 px-10 bg-[#E17F88] hover:bg-[#D16F78] text-white font-semibold rounded-xl transition-all shadow-lg shadow-[#E17F88]/30 hover:shadow-xl">
           לטופס הרישום
          </button>
        </motion.div>
      </footer>
    </div>
  );
}
